import { initializeGateway } from '@core';

import { usePatientStore } from '../patient';
import { initialState, STORE_NAME } from './data-sources-store.constant';
import {
  AddSource,
  GetSource,
  GetSourceConfig,
  GetSourceGateway,
  GetType,
  IsSourceExist,
  RemoveSource,
  ResetDataSource,
  SetType,
} from './data-sources-store.types';

export const addSource = (set: SetType, get: GetType): AddSource => {
  return async params => {
    const { sources, primarySource, isSourceExist, getSourceGateway } = get();
    if (params.projectId && isSourceExist(params.projectId)) {
      return await getSourceGateway(params.projectId);
    }

    const primarySourceId =
      usePatientStore.getState().patientInfo.primarySourceId;
    const isPrimary =
      primarySourceId === params.projectId || primarySourceId === undefined;

    const { config, gateway } = await initializeGateway({
      ...params,
      isPrimary,
    });

    if (params.projectId && isSourceExist(params.projectId)) {
      return await getSourceGateway(params.projectId);
    }
    const authenticateResult = await gateway.authenticate();
    if (!authenticateResult)
      throw new Error(
        'The source was not added because authentication failed.'
      );

    const existingPrimarySource = primarySource;

    const newValues = {
      sources: [...sources],
      primarySource: isPrimary ? gateway : existingPrimarySource,
    };

    if (!isSourceExist(params.projectId!))
      newValues.sources.push({ config, gateway });

    set(newValues, false, `${STORE_NAME}-ADD_DATA_SOURCE`);
    return gateway;
  };
};

export const getPrimarySource = (get: GetType) => () => {
  const primarySource = get().primarySource;
  if (primarySource) return primarySource;

  const { primarySourceUuid, primarySourceId } =
    usePatientStore.getState().patientInfo;
  if (!primarySourceUuid || !primarySourceId)
    throw new Error('Account is not properly logged in');

  return get().getSourceGateway(primarySourceId, primarySourceUuid);
};

export const getSource: GetSource = (get: GetType, projectId: string) => {
  return get().sources.find(item => item.config.projectId === projectId);
};

export const removeSource =
  (set: SetType, get: GetType): RemoveSource =>
  (projectId: string) => {
    const newSources = get().sources.filter(
      source => source.config.projectId !== projectId
    );
    set({ sources: newSources }, false, `${STORE_NAME}-REMOVE-SOURCE`);
  };

export const getSourceGateway = (get: GetType): GetSourceGateway => {
  return async (projectId, patientSourceUUID) => {
    const gateway = getSource(get, projectId)?.gateway;
    if (gateway) return gateway;

    const patientUUID =
      patientSourceUUID ??
      usePatientStore.getState().patientInfo.primarySourceUuid;

    return await get().addSource({
      projectId: projectId,
      patientSourceUUID: patientUUID,
    });
  };
};

export const getSourceConfig = (get: GetType): GetSourceConfig => {
  return projectId => getSource(get, projectId)?.config;
};

export const isSourceExist = (get: GetType): IsSourceExist => {
  return projectId =>
    get().sources.some(item => item.config.projectId === projectId);
};

export const resetDataSource = (set: SetType): ResetDataSource => {
  return () => set(initialState, false, `${STORE_NAME}-RESET_DATA_SOURCE`);
};
